import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { useFormik, useFormikContext } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { Fragment, memo, useEffect, useRef, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import AccordionIssuedAssessmentForm, { IssuedAssessmentVM, schema } from './form';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { PracticeDetailVM } from 'practice/view-models/practice-detail';
import { DateTime } from 'luxon';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { Outcome } from 'practice/constants/outcome';
import { transformExponent } from 'system/components/shared/street-number-list/transform-exponent';

interface IssuedAssessmentProps {
  loadUpdateDate: () => Promise<PracticeDetailVM> | void;
  isUnavailability?: boolean;
  noteMesso?: string;
}

const AccordionIssuedAssessmentComponent = ({
  loadUpdateDate,
  isUnavailability,
  noteMesso,
}: IssuedAssessmentProps): React.ReactElement => {
  const params = useParams();

  const [open, setOpen] = useState(true);

  const ref = useRef<HTMLDivElement | null>(null);

  const formContextPage = useFormikContext<PracticeDetailVM>();

  const insertData = useHttpClient(`${__PRACTICE_API_URL__}/accertamento-messi/ritorno`, {
    options: {
      method: 'POST',
      errorCatching: true,
      loading: true,
    },
  });

  const printDataUnavailability = useHttpClient<Blob>(
    `${__PRACTICE_API_URL__}/pratiche/stampa/proposta-cambio-indirizzo`,
    {
      options: {
        method: 'POST',
        errorCatching: true,
        loading: true,
      },
    }
  );

  useEffect(() => {
    ref.current?.scrollIntoView();
  }, []);

  const onSubmit = useEventCallback((values: IssuedAssessmentVM) => {
    let nuovoIndirizzoIntestatario = '';

    if (values.addressAuto?.description) {
      nuovoIndirizzoIntestatario =
        nuovoIndirizzoIntestatario + (values.addressAuto?.description || '');
    }

    if (values.number) {
      nuovoIndirizzoIntestatario = `${nuovoIndirizzoIntestatario} ${values.number || ''}`;
    }

    if (formContext.values.exponent) {
      nuovoIndirizzoIntestatario =
        nuovoIndirizzoIntestatario + transformExponent(formContext.values.exponent || '');
    }

    const data = {
      codiceEnte: params.codiceEnte || undefined,
      tipoRegistro: params.tipoRegistro || undefined,
      idAnnoPratica: params.idAnnoPratica ? Number.parseInt(params.idAnnoPratica, 10) : undefined,
      progressivoPratica: params.progressivoPratica
        ? Number.parseInt(params.progressivoPratica, 10)
        : undefined,
      // tipoPratica: params.tipoPratica || undefined,
      timestampSospensione: values.date ? values.date?.toFormat('yyyy-MM-dd') : undefined,
      tipoEsito: values.outcome || undefined,
      note: values.note || undefined,
      nuovoIndirizzoIntestatario: nuovoIndirizzoIntestatario || undefined,
      // tipoOperazione: '10',
      // codiceServizio: userProfile.datiLogin?.profile?.servizio?.key.codiceServizio || undefined,
      // codiceOperatore: userProfile.datiLogin?.key?.userName || undefined,
      // tipoStatoPratica: '1',
      // codiceSospensione: 2,
    };

    (insertData({ data }) as ResponsePromise<void>).then(async () => {
      toast.success('Operazione eseguita con successo');

      // aggiornamento stato pratica
      formContextPage.setFieldValue('practice', {
        ...(formContextPage.values.practice || {}),
        codiceStatoPratica: '1',
        tipoOperazione: '10',
      });

      // se cambia lo stato della pratica ricaricare dati
      const newData: PracticeDetailVM | void = await loadUpdateDate();

      if (newData) {
        formContextPage.setValues({
          ...(formContextPage.values || {}),
          ...newData,
        });
      }
    });
  });

  const formContext = useFormik<IssuedAssessmentVM>({
    initialValues: {
      date: DateTime.now(),
      outcome: '',
      note: '',
      noteMesso: noteMesso || '',
      addressAuto: null,
      cap: '',
    },
    validationSchema: schema,
    onSubmit,
    // enableReinitialize: true,
  });

  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });

  const printData = useEventCallback(() => {
    let nuovoIndirizzoIntestatario = '';

    if (formContext.values.addressAuto?.description) {
      nuovoIndirizzoIntestatario =
        nuovoIndirizzoIntestatario + (formContext.values.addressAuto?.description || '');
    }

    if (formContext.values.number) {
      nuovoIndirizzoIntestatario = `${nuovoIndirizzoIntestatario} ${
        formContext.values.number || ''
      }`;
    }

    if (formContext.values.exponent) {
      nuovoIndirizzoIntestatario =
        nuovoIndirizzoIntestatario + transformExponent(formContext.values.exponent || '');
    }

    const data = {
      codiceEnte: params.codiceEnte || undefined,
      tipoRegistro: params.tipoRegistro || undefined,
      idAnnoPratica: params.idAnnoPratica ? Number.parseInt(params.idAnnoPratica, 10) : undefined,
      progressivoPratica: params.progressivoPratica
        ? Number.parseInt(params.progressivoPratica, 10)
        : undefined,
      nuovoIndirizzoIntestatario: nuovoIndirizzoIntestatario || undefined,
      capNuovoIndirizzoIntestatario: formContext.values.cap,
    };

    (printDataUnavailability({ data, responseType: 'blob' }) as ResponsePromise<Blob>).then(
      (response) => {
        const pdfCertificate = response?.data;

        if (pdfCertificate) {
          const blob = new Blob([pdfCertificate], { type: 'application/pdf' });
          blobToBase64(blob).then((base64) => {
            setPdfModal({
              data: base64.split(',')[1],
              name: 'Stampa proposta cambiamento indirizzo',
              open: true,
            });
          });
        }
        toast.success('Operazione eseguita con successo');
      }
    );
  });

  return (
    <Fragment>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
        ref={ref}
      >
        <AccordionSummary
          aria-controls="panel-issued-assessment-content"
          id="panel-issued-assessment-header"
          expandIcon={<ExpandMore />}
        >
          <Typography>Registra accertamento messi</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AccordionIssuedAssessmentForm
            values={formContext.values}
            onChange={formContext.setFieldValue}
            onChanges={(values) => {
              formContext.setValues({
                ...formContext.values,
                ...values,
              });
            }}
            errors={formContext.errors}
            touched={formContext.touched}
            isUnavailability={isUnavailability}
          />
          <div className="pmi-d-flex">
            <div className="pmi-align-right pmi-pt-2">
              <PmiButton
                color="cdmBrandThird"
                variant="outlined"
                onClick={() => formContext.handleSubmit()}
                id="return"
              >
                Conferma
              </PmiButton>
            </div>
            {isUnavailability && formContext.values.outcome === Outcome.CambioIndirizzo ? (
              <div className="pmi-align-right pmi-pl-2 pmi-pt-2">
                <PmiTooltipError
                  show={!formContext.values.addressAuto || !formContext.values.cap}
                  message={
                    !formContext.values.addressAuto || !formContext.values.cap
                      ? 'Inserire indirizzo e cap'
                      : ''
                  }
                >
                  <span>
                    <PmiButton
                      color="cdmBrandThird"
                      variant="outlined"
                      onClick={printData}
                      id="print"
                      disabled={!formContext.values.addressAuto || !formContext.values.cap}
                    >
                      Stampa proposta cambiamento indirizzo
                    </PmiButton>
                  </span>
                </PmiTooltipError>

                <ModalViewerPDF
                  open={pdfModal.open}
                  data={pdfModal.data}
                  name={pdfModal.name}
                  title="Stampa proposta cambiamento indirizzo"
                  onClose={() => {
                    setPdfModal({ data: '', open: false, name: '' });
                  }}
                />
              </div>
            ) : undefined}
          </div>
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};

AccordionIssuedAssessmentComponent.displayName = 'Accordion issued assessment component';

const AccordionIssuedAssessment = memo(AccordionIssuedAssessmentComponent);

export default AccordionIssuedAssessment;
